import type { JSX } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SaveIcon from '@mui/icons-material/Save';
import type { GridRenderCellParams, GridRowModel } from '@mui/x-data-grid-pro';
import {
  GridActionsCell,
  GridActionsCellItem,
  gridEditRowsStateSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';

import useAuth from 'portal/src/auth/useAuth';

import { InRowEditActionColumnProps } from './addActionColumnProps';

const saveLabel = 'Save Edit';
const cancelLabel = 'Cancel Edit';
const editLabel = 'Edit';
const editInRowLabel = 'Edit In Row';
const deleteLabel = 'Delete';

const InRowEditActionColumn = <TRow extends GridRowModel = GridRowModel>({
  inRowEditConfig,
  params,
}: InRowEditActionColumnProps<TRow> & {
  params: GridRenderCellParams<TRow>;
}): JSX.Element => {
  const { hasReadOnlyAccess } = useAuth();
  const gridApiRef = useGridApiContext();
  const rowModesModel = useGridSelector(gridApiRef, gridEditRowsStateSelector);

  const { id } = params;
  const isInEditRowMode = typeof rowModesModel[id] !== 'undefined';

  const {
    downloadHref,
    downloadLabel = 'Download file',
    isDeleteDisabled = false,
    isEditDisabled = false,
    useEditIcon = false,
    editHiddenForRow,
    onSaveClick,
    onDeleteClick,
    onCancelClick,
    overrideEditBtn,
  } = inRowEditConfig(params.row);

  const otherRowInEditMode = Object.keys(rowModesModel).find(
    (rowId) => rowId !== id,
  );

  const isDisabled =
    hasReadOnlyAccess() || (!isInEditRowMode && !!otherRowInEditMode);

  const editingCells = Object.values(rowModesModel[id] ?? {});
  const isSaveDisabled =
    isDisabled ||
    editingCells.some(
      (cellProps) => !!cellProps?.error || cellProps?.isProcessingProps,
    );
  const hasMoreMenuActions = onDeleteClick && downloadHref;
  const getMoreMenuActionsProps = (label: string) =>
    hasMoreMenuActions ? { showInMenu: true } : { title: label };

  return (
    <GridActionsCell {...params}>
      {isInEditRowMode ? (
        <>
          <GridActionsCellItem
            disabled={isSaveDisabled}
            icon={<SaveIcon />}
            label={saveLabel}
            title={saveLabel}
            onClick={() => {
              const shouldStopEditMode = onSaveClick?.() !== false;

              if (!shouldStopEditMode) {
                return;
              }

              gridApiRef.current.stopRowEditMode({ id });
            }}
          />
          <GridActionsCellItem
            disabled={isDisabled}
            icon={<CancelIcon />}
            label={cancelLabel}
            title={cancelLabel}
            onClick={() => {
              onCancelClick?.();
              gridApiRef.current.stopRowEditMode({
                id,
                ignoreModifications: true,
              });
            }}
          />
        </>
      ) : !editHiddenForRow ? (
        <>
          <GridActionsCellItem
            disabled={isDisabled || isEditDisabled}
            icon={
              useEditIcon ? (
                <EditIcon />
              ) : (
                <EditNoteIcon sx={{ fontSize: 25 }} />
              )
            }
            label={overrideEditBtn ? editLabel : editInRowLabel}
            title={overrideEditBtn ? editLabel : editInRowLabel}
            onClick={() => {
              if (overrideEditBtn) {
                overrideEditBtn();
              } else {
                gridApiRef.current.startRowEditMode({ id });
              }
            }}
          />
          {downloadHref && (
            <GridActionsCellItem
              icon={<DownloadIcon fontSize="small" />}
              label={downloadLabel}
              {...getMoreMenuActionsProps(downloadLabel)}
              // MUI X types omit `href`, but the underlying button forwards it
              {...({ href: downloadHref } as object)}
            />
          )}
          {onDeleteClick && (
            <GridActionsCellItem
              disabled={isDisabled || isDeleteDisabled}
              icon={<DeleteIcon />}
              label={deleteLabel}
              {...getMoreMenuActionsProps(deleteLabel)}
              onClick={onDeleteClick}
            />
          )}
        </>
      ) : null}
    </GridActionsCell>
  );
};

export default InRowEditActionColumn;
